import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";

import {
  type Address,
  type DeliveryAddress,
  WalleyPaymentType,
} from "./Checkout.types";
import type { DeliveryOption } from "./delivery/Delivery.utils";

export type CheckoutStore = {
  counter: number;
  address: Partial<Address>;
  ableToComplete: boolean;
  paymentType: WalleyPaymentType;
  hasLine2Address: boolean;
  deliveryAddress: Partial<DeliveryAddress>;
  deliveryOption?: DeliveryOption;
  hasSeparateDeliveryAddress: boolean;
  canScrollToDelivery: boolean;
  hasSetAddress: boolean;
  setAddress: (newAddress: Partial<Address>) => Promise<unknown>;
  setAbleToComplete: (value: boolean) => void;
  setPaymentType: (paymentType: WalleyPaymentType) => void;
  setDeliveryOption: (deliveryOption?: DeliveryOption) => void;
  setDeliveryAddress: (
    newDeliveryAddress: Partial<DeliveryAddress>,
  ) => Promise<unknown>;
  setHasLine2Address: (hasLine2Address: boolean) => void;
  setHasSeparateDeliveryAddress: (hasSeparateDeliveryAddress: boolean) => void;
  setCanScrollToDelivery: (canScrollToDelivery: boolean) => void;
  destroy: () => void;
};

export const CHECKOUT_STORAGE_KEY = "ng_checkout";

export const checkoutStore = create<CheckoutStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set) => ({
          /**
           * Set default values for the store
           */
          counter: 0,
          address: {},
          ableToComplete: true,
          paymentType: WalleyPaymentType.b2c,
          deliveryOption: undefined,
          deliveryAddress: {},
          hasLine2Address: false,
          hasSeparateDeliveryAddress: false,
          canScrollToDelivery: false,
          hasSetAddress: false,
          /**
           *
           * @param newAddress - The new address
           * @returns - void
           */
          setAddress: (newAddress) => {
            return Promise.resolve(
              set((state) => {
                return {
                  counter: state.counter + 1,
                  hasSetAddress: true,
                  address: {
                    ...state.address,
                    ...newAddress,
                    zipCode: newAddress.zipCode?.trim(),
                  },
                };
              }),
            );
          },
          setAbleToComplete: (value: boolean) => {
            if (value === true) {
              set({ ableToComplete: true });
              return;
            }

            set({
              ableToComplete: false,
              deliveryOption: undefined,
            });
          },
          /**
           * Set whether the address has a C/O address
           * @param hasLine2Address - Whether the address has a C/O address
           * @returns - void
           */
          setHasLine2Address: (hasLine2Address: boolean) => {
            set({ hasLine2Address });
          },
          /**
           *
           * @param newDeliveryAddress - The new delivery address
           * @returns - void
           */
          setDeliveryAddress: (newDeliveryAddress) => {
            return Promise.resolve(
              set((state) => {
                return {
                  counter: state.counter + 1,
                  deliveryAddress: {
                    ...state.deliveryAddress,
                    ...newDeliveryAddress,
                    deliveryZipCode: newDeliveryAddress.deliveryZipCode?.trim(),
                  },
                };
              }),
            );
          },
          /**
           *
           * @param hasSeparateDeliveryAddress - Whether the customer has a separate delivery address
           * @returns - void
           */
          setHasSeparateDeliveryAddress: (
            hasSeparateDeliveryAddress: boolean,
          ) => {
            set({ hasSeparateDeliveryAddress });
          },
          /**
           *
           * @param deliveryOption - The delivery option selected by the customer in nShift
           * @returns - void
           */
          setDeliveryOption: async (deliveryOption) => {
            if (!deliveryOption) {
              set((state) => ({
                deliveryOption: undefined,
                counter: state.counter + 1,
              }));
              return;
            }
            window.walley?.checkout?.api?.suspend?.();
            set((state) => ({ deliveryOption, counter: state.counter + 1 }));
          },
          setPaymentType: (paymentType) => {
            set(() => ({ paymentType }));
          },
          setCanScrollToDelivery: (canScrollToDelivery) => {
            set(() => ({ canScrollToDelivery }));
          },
          destroy: () =>
            set({
              deliveryOption: undefined,
              ableToComplete: false,
              hasSetAddress: false,
              counter: 0,
            }),
        }),
        {
          merge: (persistedState: any, currentState) => {
            const newState = {
              ...currentState,
              ...persistedState,
            };
            if (!newState.hasSetAddress) {
              newState.deliveryOption = undefined;
            }
            return newState;
          },
          // Persist parts of the store in local storage
          name: CHECKOUT_STORAGE_KEY,
          partialize: (state) => {
            return Object.fromEntries(
              Object.entries(state).filter(([key]) => {
                return [
                  "address",
                  "paymentType",
                  "deliveryOption",
                  "deliveryAddress",
                  "hasSeparateDeliveryAddress",
                  "hasSetAddress",
                ].includes(key);
              }),
            );
          },
        },
      ),
    ),
  ),
);
